import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { shopsAPI } from "../services/api";
import "./Layout.css";

interface LayoutProps {
  children: React.ReactNode;
}

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed"; platform: string }>;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const { user, logout, isAdmin, isAgent, isShopOwner } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [totalDebt, setTotalDebt] = useState<number | null>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [installPrompt, setInstallPrompt] =
    useState<BeforeInstallPromptEvent | null>(null);

  useEffect(() => {
    const handleBeforeInstall = (e: Event) => {
      e.preventDefault();
      setInstallPrompt(e as BeforeInstallPromptEvent);
    };
    window.addEventListener("beforeinstallprompt", handleBeforeInstall);
    return () => {
      window.removeEventListener("beforeinstallprompt", handleBeforeInstall);
    };
  }, []);

  const handleInstallClick = async () => {
    if (!installPrompt) return;
    await installPrompt.prompt();
    const choiceResult = await installPrompt.userChoice;
    if (choiceResult.outcome === "accepted") {
      setInstallPrompt(null);
    }
  };

  useEffect(() => {
    if (!isShopOwner()) return;
    shopsAPI
      .getAll({ limit: 200 })
      .then((res) => {
        const debt = res.data.reduce((s, sh) => s + Number(sh.debt ?? 0), 0);
        setTotalDebt(debt);
      })
      .catch(() => {});
  }, [isShopOwner, location.pathname]);

  // Close drawer on navigation
  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const fmt = (n: number) =>
    n.toLocaleString("ru-RU", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

  const debtLabel =
    totalDebt !== null
      ? totalDebt > 0
        ? `Долг: ${fmt(totalDebt)} с`
        : totalDebt < 0
          ? `Аванс: +${fmt(Math.abs(totalDebt))} с`
          : "✓ Долгов нет"
      : null;

  const navLinks = (
    <>
      <Link to="/" className={location.pathname === "/" ? "active" : ""}>
        Главная
      </Link>
      <Link
        to="/categories"
        className={location.pathname === "/categories" ? "active" : ""}
      >
        Категории
      </Link>
      <Link
        to="/products"
        className={location.pathname === "/products" ? "active" : ""}
      >
        Товары
      </Link>
      <Link
        to="/stock"
        className={location.pathname === "/stock" ? "active" : ""}
      >
        Склад
      </Link>
      <Link
        to="/shops"
        className={location.pathname === "/shops" ? "active" : ""}
      >
        Магазины
      </Link>
      {isAdmin() && (
        <Link
          to="/users"
          className={location.pathname === "/users" ? "active" : ""}
        >
          Пользователи
        </Link>
      )}
      {isAdmin() && (
        <Link
          to="/orders-management"
          className={location.pathname === "/orders-management" ? "active" : ""}
        >
          Управление заказами
        </Link>
      )}
      {(isAgent() || isShopOwner()) && (
        <Link
          to="/orders"
          className={location.pathname === "/orders" ? "active" : ""}
        >
          Мои заказы
        </Link>
      )}
    </>
  );

  return (
    <div className="layout">
      {/* Mobile backdrop */}
      {menuOpen && (
        <div
          className="navbar-backdrop"
          onClick={() => setMenuOpen(false)}
          aria-hidden="true"
        />
      )}

      <nav className="navbar">
        {/* Desktop + mobile top bar */}
        <div className="navbar-top">
          <div className="navbar-brand">
            <Link to="/" className="navbar-brand-link">
              <h2>Wilsun</h2>
            </Link>
          </div>

          {/* Desktop menu (hidden on mobile via CSS) */}
          <div className="navbar-menu">{navLinks}</div>

          <div className="navbar-user">
            {installPrompt && (
              <button
                type="button"
                onClick={handleInstallClick}
                className="btn-install-pwa desktop-only"
                title="Установить приложение на устройство"
              >
                📲 Установить
              </button>
            )}
            {isShopOwner() && debtLabel && (
              <Link
                to="/shops"
                className={`navbar-debt ${totalDebt! > 0 ? "debt-positive" : "debt-zero"}`}
              >
                {debtLabel}
              </Link>
            )}
            <span>
              {user?.firstName} {user?.lastName}
            </span>
            <button onClick={handleLogout} className="logout-btn desktop-only">
              Выйти
            </button>
          </div>

          {/* Burger (visible on mobile only) */}
          <button
            type="button"
            className={`burger${menuOpen ? " burger-open" : ""}`}
            onClick={() => setMenuOpen((o) => !o)}
            aria-label="Меню"
            aria-expanded={menuOpen}
          >
            <span className="burger-line" />
            <span className="burger-line" />
            <span className="burger-line" />
          </button>
        </div>

        {/* Mobile dropdown drawer */}
        <div className={`navbar-drawer${menuOpen ? " drawer-open" : ""}`}>
          {installPrompt && (
            <div className="drawer-install-box">
              <button
                type="button"
                onClick={handleInstallClick}
                className="btn-install-pwa full-width"
              >
                📲 Установить приложение
              </button>
            </div>
          )}
          {isShopOwner() && debtLabel && (
            <div className="drawer-debt">
              <Link
                to="/shops"
                className={`navbar-debt ${totalDebt! > 0 ? "debt-positive" : "debt-zero"}`}
              >
                {debtLabel}
              </Link>
            </div>
          )}
          <div className="drawer-links" onClick={() => setMenuOpen(false)}>
            {navLinks}
          </div>
          <div className="drawer-user-row">
            <span className="drawer-username">
              {user?.firstName} {user?.lastName}
            </span>
            <button onClick={handleLogout} className="logout-btn">
              Выйти
            </button>
          </div>
        </div>
      </nav>

      <main className="main-content">{children}</main>
    </div>
  );
};

export default Layout;
